import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
const UPDATED = "June 2026";

function LegalShell({ title, testId, desc, children }) {
  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta ? meta.getAttribute("content") : null;
    if (meta && desc) meta.setAttribute("content", desc);
    return () => { if (meta && prev != null) meta.setAttribute("content", prev); };
  }, [desc]);
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2 font-heading font-bold" data-testid="legal-home-link">
            <img src="/logo-mark.webp" alt="Send-Proof" className="h-7 w-7 rounded-lg" />
            Send-Proof
          </Link>
          <Link to="/" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-10" data-testid={testId}>
        <h1 className="font-heading text-3xl font-bold sm:text-4xl">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: {UPDATED}</p>
        <div className="mt-8 space-y-6 text-sm leading-relaxed text-foreground/90 [&_h2]:mt-8 [&_h2]:font-heading [&_h2]:text-lg [&_h2]:font-bold [&_p]:mt-2 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5">
          {children}
        </div>
        <p className="mt-10 rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground">
          This page is provided for general information and is not legal advice. Please have a qualified attorney review these documents before you rely on them for your business.
        </p>
      </main>
    </div>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of Service" testId="terms-page" desc="Read the Send-Proof Terms of Service: how accounts, reports, share links, photo proof, billing, cancellation, and acceptable use work across the service.">
      <p>Welcome to Send-Proof (“Send-Proof,” “we,” “us,” or “our”). These Terms of Service (“Terms”) govern your access to and use of the Send-Proof website, applications, and services (collectively, the “Service”). By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.</p>

      <h2>1. The Service</h2>
      <p>Send-Proof lets account owners create checklists (“reports”) of work items, share a link with a worker, and receive photo proof and status updates for each item. Features, availability, and limits may change over time.</p>

      <h2>2. Accounts &amp; Eligibility</h2>
      <p>You must be at least 18 years old and able to form a binding contract. You are responsible for your account credentials and for all activity that occurs under your account. Notify us promptly of any unauthorized use.</p>

      <h2>3. Subscriptions, Trials &amp; Billing</h2>
      <ul>
        <li>Send-Proof is offered on a paid subscription of $9.99 per month following a 15-day free trial, unless otherwise stated at checkout.</li>
        <li>Payments are processed by our payment provider, Stripe. By subscribing you authorize recurring charges to your payment method until you cancel.</li>
        <li>You may cancel at any time from your account billing portal; cancellation takes effect at the end of the current billing period. Except where required by law, payments are non-refundable.</li>
        <li>We may change pricing on prospective notice; continued use after a change means you accept the new price.</li>
      </ul>

      <h2>4. Share Links &amp; Worker Content</h2>
      <p>Share links let anyone with the link view a report and upload proof photos and status without an account. You are responsible for who you send links to and for the content uploaded to your reports. Do not upload unlawful, infringing, or sensitive personal content you are not authorized to share.</p>

      <h2>5. Acceptable Use</h2>
      <ul>
        <li>No illegal, harmful, harassing, or fraudulent activity.</li>
        <li>No attempts to breach security, scrape, overload, or reverse-engineer the Service.</li>
        <li>No uploading of malware or content that violates the rights of others.</li>
      </ul>

      <h2>6. Your Content</h2>
      <p>You retain ownership of the photos, notes, and other content you submit (“Your Content”). You grant Send-Proof a limited license to host, process, and display Your Content solely to operate and improve the Service. You represent that you have the rights necessary to submit Your Content.</p>

      <h2>7. Intellectual Property</h2>
      <p>The Service, including its software, design, and trademarks, is owned by Send-Proof and its licensors. These Terms do not grant you any rights to our branding except as needed to use the Service.</p>

      <h2>8. Third-Party Services</h2>
      <p>We rely on third parties (e.g., Stripe for payments, Supabase for data and storage hosting, and push/notification providers). Your use of the Service may be subject to their terms, and we are not responsible for third-party services.</p>

      <h2>9. Disclaimers</h2>
      <p>The Service is provided “as is” and “as available” without warranties of any kind, whether express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, secure, or error-free.</p>

      <h2>10. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law, Send-Proof will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, profits, or goodwill. Our total liability for any claim relating to the Service will not exceed the amounts you paid us in the 12 months before the claim.</p>

      <h2>11. Termination</h2>
      <p>You may stop using the Service at any time. We may suspend or terminate your access if you violate these Terms or if necessary to protect the Service or other users. Upon termination, your right to use the Service ends.</p>

      <h2>12. Changes to These Terms</h2>
      <p>We may update these Terms from time to time. Material changes will be posted here with an updated date. Continued use after changes take effect constitutes acceptance.</p>

      <h2>13. Contact</h2>
      <p>Questions about these Terms? Contact us at support@send-proof.com.</p>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy" testId="privacy-page" desc="The Send-Proof privacy policy: the account and photo data we collect, how share links and proof photos are stored, and the choices you have over your data.">
      <p>This Privacy Policy explains how Send-Proof (“we,” “us,” or “our”) collects, uses, and shares information when you use our website, applications, and services (the “Service”).</p>

      <h2>1. Information We Collect</h2>
      <ul>
        <li><b>Account information:</b> name, email address, and authentication details when you sign up or sign in (including via Google, if you choose).</li>
        <li><b>Content you provide:</b> jobs, reports, checklist items, notes, and photos (including proof photos uploaded via share links).</li>
        <li><b>Billing information:</b> subscription status and customer identifiers. Card details are handled directly by Stripe; we do not store full card numbers.</li>
        <li><b>Usage &amp; device data:</b> log data, approximate country (from network headers), browser/OS type, and share-link view events used for tracking and product analytics.</li>
        <li><b>Push notification tokens:</b> if you enable notifications, we store the subscription needed to deliver them.</li>
      </ul>

      <h2>2. How We Use Information</h2>
      <ul>
        <li>To provide, operate, and improve the Service.</li>
        <li>To process subscriptions and payments.</li>
        <li>To send transactional messages and, if enabled, push notifications about report activity.</li>
        <li>To secure the Service, prevent abuse, and comply with legal obligations.</li>
      </ul>

      <h2>3. How We Share Information</h2>
      <ul>
        <li><b>Service providers:</b> Stripe (payments), Supabase (database and file storage hosting), and notification/email providers, who process data on our behalf.</li>
        <li><b>Share links:</b> content in a report is visible to anyone you give the link to.</li>
        <li><b>Legal:</b> when required by law or to protect rights, safety, and security.</li>
      </ul>
      <p>We do not sell your personal information.</p>

      <h2>4. Data Storage &amp; Security</h2>
      <p>Data and uploaded photos are stored with our hosting provider using access controls and private storage. Proof and reference images are served via short-lived signed links. No method of transmission or storage is 100% secure, but we take reasonable measures to protect your information.</p>

      <h2>5. Data Retention</h2>
      <p>We retain information for as long as your account is active or as needed to provide the Service, comply with legal obligations, resolve disputes, and enforce agreements. You may request deletion of your account and associated content.</p>

      <h2>6. Your Choices &amp; Rights</h2>
      <ul>
        <li>Access, update, or delete your account information from within the app or by contacting us.</li>
        <li>Turn push notifications on or off at any time in your account settings.</li>
        <li>Depending on your location, you may have rights to access, correct, delete, or port your data, or to object to certain processing.</li>
      </ul>

      <h2>7. Children</h2>
      <p>The Service is not directed to children under 13 (or the minimum age in your jurisdiction), and we do not knowingly collect their information.</p>

      <h2>8. International Users</h2>
      <p>We may process and store information in countries other than where you live. By using the Service, you consent to such transfers consistent with this Policy.</p>

      <h2>9. Changes to This Policy</h2>
      <p>We may update this Policy from time to time. Material changes will be posted here with an updated date.</p>

      <h2>10. Contact</h2>
      <p>Questions or requests? Contact us at support@send-proof.com.</p>
    </LegalShell>
  );
}
